<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:settings-2',
	name: '$t:blocks.input_slider.name',
	description: '$t:blocks.input_slider.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ({ primitiveType }) => {
		if (isPrimitiveTypeDecimal(primitiveType)) return [];
		return ['number'];
	}, recommendedDisplays: ['$builtin:DisplayFormattedValue']	},

	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'min',
		},
		{
			name: 'max',
		},
		{
			name: 'step',
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'block',
		},
		{
			name: 'disabled',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'readonly',
		},
	],
}
</script>
<script lang="ts" setup>
import type { SliderProps } from '@nuxt/ui';
import type { NumberFieldOptions } from '#layers/data-model';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';
import { isPrimitiveTypeDecimal } from '#layers/data-model';
import { useDisabledNotice, useReadonly } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

interface Props extends
	Pick<NumberFieldOptions, 'min' | 'max' | 'step'>,
	Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'>,
	Pick<SliderProps, 'color' | 'size'> {
	/**
	 * Render the input full width
	 */
	block?: boolean;
}

const props = defineProps<Props>();
const model = defineModel<number>();

;

const forwarded = computed(() => ({
	...reactivePick(props, 'color', 'size'),
	min: props.min !== undefined ? Number(props.min) : undefined,
	max: props.max !== undefined ? Number(props.max) : undefined,
	step: props.step !== undefined ? Number(props.step) : undefined,
}));
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DSlider
			v-model="modelValue"
			v-bind="forwarded"
			tooltip
			:disabled
			:aria-readonly="readonly"
			:class="{
				'w-full flex-1': block,
				'pointer-events-none': readonly,
			}"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
	</DTooltip>
</template>
